import { AidaPayloadService } from './../../shared/services/aida/aida-payload/aida-payload.service';
import {
  Component,
  Inject,
  ChangeDetectorRef,
  AfterContentChecked,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { ModalRef, ModalData } from '@purespectrum1/ui/modal';
import { CombinedQualification } from '../../shared/interfaces/qualification.interface';
import {
  NOT_TO_BE_GROUPED_QUALIFICATIONS,
  QUALIFICATION_CODES,
  LOCATION_QUALIFICATION_CODES,
  LOCATION_ALL_QUALI_CODES,
} from '../../shared/constants/qualification-code';
import { Constants } from '../../shared/constants/create-survey.constants';
import { Flexible } from '../../shared/interfaces/create-surveys.interface';
import { SelectedQuota } from '../../shared/interfaces/quota.interface';
import { QuotaModalService } from '../quota-modal/quota-modal.service';
import { DistributionQuotaRule } from './domain/distribution-quota-rule';
import { AidaPayload } from '../../shared/interfaces/aida.interface';
import { filter } from 'rxjs/operators';
import { notifyMessage } from '../../shared/constants/notify-message';
import { deepCopy } from '../utils/deep-copy';
import { Subscription } from 'rxjs';
import { sortDataByFieldName } from '../../shared/utils/sort-data';

interface AidaQualificationDetails extends AidaPayload {
  isSelectedForAida: boolean;
  appliedQuotas: SelectedQuota[];
  isAidaError: boolean;
}

@Component({
  selector: 'ps-ui-quota-modal',
  templateUrl: './quota-modal.component.html',
  styleUrls: ['./quota-modal.component.css'],
})
export class QuotaModalComponent
  implements AfterContentChecked, OnInit, OnDestroy
{
  readonly QUALIFICATION_CODES = QUALIFICATION_CODES;
  readonly CHILDREN_QUALIFICATION_CODES = [
    QUALIFICATION_CODES.CHILDREN,
    QUALIFICATION_CODES.CHILD_AGE_GENDER,
  ];
  public allowDistribution: boolean = true;
  data = this._modalData;
  remaning: number = this.data.complete;
  public groupedQuotas!: SelectedQuota[];
  public showGroupingButton: boolean = true;
  public showGroupingDetails: boolean = false;
  public showGroupButton: boolean = false;
  public grouping: boolean = false;
  public totalCompletes: number = 0;
  public hasNestedGroup: boolean = false;

  private _aida: AidaQualificationDetails = {
    enabled: false,
    selected_quota_ids: [],
    isSelectedForAida: false,
    appliedQuotas: [],
    isAidaError: false,
  };
  private _subscription: Subscription = new Subscription();
  public get isCensusApplied() {
    return this._modalData.isCensusApplied;
  }

  public get qualificationCode() {
    return this._modalData.qualification.qualification_code;
  }

  public get isChildrenQualification() {
    return this.CHILDREN_QUALIFICATION_CODES.includes(
      this.data.qualification.qualification_code
    );
  }

  constructor(
    private _modalRef: ModalRef<
      QuotaModalComponent,
      {
        msg: string;
        flexible: Flexible;
        qualification: CombinedQualification;
        quotas: SelectedQuota[];
        isMc: boolean;
        isSelectedForAida: boolean;
      }
    >,
    @Inject(ModalData)
    private _modalData: {
      complete: number;
      remaining: number;
      clickBalance: number;
      qualification: CombinedQualification;
      quotas: SelectedQuota[];
      flexible: Flexible;
      isAdvancedTargeting: boolean;
      isCensusApplied: boolean;
      hasNestedGroup?: boolean;
      isLiveSurveyEdit: boolean;
      isMcSurvey?: boolean;
      isSelectedForAida?: boolean;
    },
    private _cdref: ChangeDetectorRef,
    private _quotaModalService: QuotaModalService,
    private _aidaPayloadService: AidaPayloadService
  ) {}

  ngOnInit() {
    this._hadleAidaPayload();
    if (this.qualificationCode === QUALIFICATION_CODES.CHILD_AGE_GENDER) {
      this.data.quotas = this._quotaModalService.mapChildAgeGender(
        this.data.quotas
      );
    }
    if (
      this.qualificationCode === QUALIFICATION_CODES.CHILD_AGE_GENDER &&
      this.data.quotas.length &&
      this.data.quotas[0]?.number
    ) {
      // add empty range row on top for child Children in live edit page
      this.data.quotas?.unshift({
        max: 0,
        min: 0,
        number: 0,
        percentage: 0,
        maximum: 0,
        minimum: 0,
        gender: undefined,
        text: 'Have Children',
        ageunit: undefined,
      });
    }
    this.verifyDistribution();
    this.showGroupingDetails = this.showGroupButton = this.data.quotas?.some(
      (quota) =>
        quota.selectedForGroup &&
        quota.oldQuotasText &&
        quota.oldQuotasText.length <= 1
    );
    if (this.showGroupingDetails) {
      this.updateQuotas(this.data.quotas);
    }
    if (
      NOT_TO_BE_GROUPED_QUALIFICATIONS.includes(
        this.data.qualification.qualification_code
      ) ||
      this.data.qualification.is_range
    ) {
      this.showGroupingButton = false;
      this.showGroupingDetails = false;
    }
  }

  ngAfterContentChecked() {
    this._cdref.detectChanges();
  }

  private _hadleAidaPayload() {
    const _aidaSubscription = this._aidaPayloadService
      .getAidaLiveEditPayload()
      .pipe(filter(() => !!this.data.isSelectedForAida))
      .subscribe((payload) => {
        const appliedQuotas = deepCopy(this.data.quotas);
        this._aida = {
          ...payload,
          appliedQuotas,
          isSelectedForAida: !!this.data.isSelectedForAida,
          isAidaError: false,
        };
      });

    this._subscription.add(_aidaSubscription);
  }

  verifyDistribution() {
    this.allowDistribution = new DistributionQuotaRule(
      this.data.qualification.qualification_code,
      this.data.quotas
    ).allow();
  }

  onGroupConditions(): void {
    this.grouping = true;
    this.showGroupingDetails = true;
  }

  applyQuota(msg: string, isMc: boolean = false) {
    if (this._aida.isAidaError) {
      this.data.quotas = this._aida.appliedQuotas;
      this._aidaPayloadService.sendAidaError(
        notifyMessage.errorMessage.AIDA.LIVE_QUOTA_CHANGE_RESTRICTION
      );
    } else {
      this._unselectQualificationWithoutQuota();
      this._reCalculateQuotaPercentageForCAGQuota();
    }

    this._modalRef.close({
      msg: msg,
      qualification: this.data.qualification,
      quotas: this.data.quotas,
      flexible: this.data.flexible,
      isMc,
      isSelectedForAida: !!this.data?.isSelectedForAida,
    });
  }

  private _reCalculateQuotaPercentageForCAGQuota() {
    if (
      this.data.qualification?.db_qualification_code ===
        QUALIFICATION_CODES.CHILD_AGE_GENDER &&
      this.data.quotas.length
    ) {
      this.data.quotas.forEach((quota: SelectedQuota) => {
        quota.percentage = Math.round(
          (quota.number / this.data.complete) * 100
        );
      });
    }
  }

  private _unselectQualificationWithoutQuota() {
    if (this.data.quotas.length) {
      for (const [quotaIndex, quota] of this.data.quotas.entries()) {
        let index = -1;
        if (
          (!quota.number && !this.isChildrenQualification) ||
          (quotaIndex && !quota.number)
        ) {
          if (
            LOCATION_QUALIFICATION_CODES.includes(
              this.data.qualification.qualification_code
            )
          ) {
            index = this.data.qualification.data.findIndex((item) =>
              quota.options?.includes(item.id)
            );
          } else {
            index = this.data.qualification.data.findIndex(
              (item) => item.text === quota.text
            );
          }
          this.data.qualification.data[index].selected = false;
          quota.selected = false;
        }
      }
    }
  }

  flexibleFlagChange(flexible: boolean) {
    if (flexible) {
      this.data.flexible = { ...this.data.flexible, enable: true };
      this.flexibleValueChange();
    } else {
      const modifiedQuotas = this.data.quotas.map((quota) => {
        quota.minimum = quota.number;
        quota.maximum = quota.number;
        return quota;
      });
      this.data = { ...this.data, quotas: modifiedQuotas };
      this.data.flexible = { ...this.data.flexible, enable: flexible };
    }
  }

  flexibleValueChange() {
    if (this.data.flexible.value > 100) {
      this.data.flexible.value = 0;
      return;
    }

    if (!this.data.flexible.enable) {
      return;
    }

    const modifiedQuotas = this.data.quotas.map((quota) => {
      let [minimum, maximum] = this._quotaModalService.getFlexibleValue(
        quota.number,
        this.data.flexible
      );
      quota.minimum = minimum;
      quota.maximum = maximum;
      return quota;
    });

    this.data = { ...this.data, quotas: modifiedQuotas };
  }

  updateQuotas(quotas: SelectedQuota[]) {
    this.remaning = this.data.complete;
    const allocatedCompletes = quotas.reduce((previous, current, index) => {
      if (
        index ||
        (!this.data.qualification.is_range && !this.isChildrenQualification)
      ) {
        return previous + Number(current.number);
      }
      return previous;
    }, 0);
    this.remaning = this.remaning - allocatedCompletes;
    this.data.remaining = this.remaning;
    if (!this._shouldUpdateQuotas(quotas)) {
      this._aida.isAidaError = true;
      return;
    }
    this._aida.isAidaError = false;
    this.data = { ...this.data, quotas };
    this.verifyDistribution();
  }

  /**
   * check for aida, and validate if we can apply quota or not
   * @param allQuotas : applied quotas
   * @returns
   */
  private _shouldUpdateQuotas(allQuotas: SelectedQuota[]): boolean {
    if (!this._aida.isSelectedForAida) {
      return true;
    }

    let quotas = allQuotas;
    if (this.data.qualification.is_range || this.isChildrenQualification) {
      const [, ...restQuotas] = allQuotas;
      quotas = restQuotas;
    }
    const aidaQuotas = this._aida.selected_quota_ids;
    const filteredQuotas = quotas.filter((quota) => quota.number > 0);
    return (
      filteredQuotas.length === aidaQuotas?.length &&
      filteredQuotas.every((quota) =>
        aidaQuotas?.includes(quota.buyer_quota_id!)
      )
    );
  }

  validValue(value: number | string) {
    if (!value && value !== 0) {
      return false;
    }
    return true;
  }

  toShowGroupingDetails(response: boolean) {
    this.showGroupingDetails = response;
    if (!response) {
      this.grouping = false;
    }
  }

  getGroupedQuotas(response: SelectedQuota[]) {
    const groupedQuotas = response;
    const result = Object.assign(
      groupedQuotas.reduce((a: SelectedQuota, b: SelectedQuota) => ({
        text: a.text + Constants.GROUP_QUOTA_SEPARATOR + b.text,
        number: Number(a.number) + Number(b.number),
        percentage: Number(a.percentage) + Number(b.percentage),
        minimum: a.minimum + b.minimum,
        maximum: a.maximum + b.maximum,
        selected: true,
        selectedForGroup: true,
      }))
    );
    result.oldQuotasText = groupedQuotas.map(
      (groupedQuota) => groupedQuota.text
    );
    this.data.quotas.push(result);
    const dbQual = this.data?.qualification?.db_qualification_code;
    if (dbQual && LOCATION_ALL_QUALI_CODES.includes(dbQual)) {
      sortDataByFieldName(this.data.quotas, 'text');
    }
  }

  distributeEquallyPercentage() {
    if (!this._hasQuotas()) {
      return;
    }

    this.clearDistribution();
    const { quotas, totalCompletes, completesNeeded } =
      this._quotaModalService.distributeEquallyPercentage(
        this.data.quotas,
        this.data.qualification,
        this.data.complete
      );
    this.data = { ...this.data, quotas };
    this.remaning = completesNeeded;
    this.totalCompletes = totalCompletes;
    this._updateQuotasValue();
  }

  private _hasQuotas() {
    return this.data.quotas.length > 1;
  }

  private _updateQuotasValue() {
    this.data.quotas.forEach((quota) => {
      let [minimum, maximum] = this._quotaModalService.getFlexibleValue(
        quota.number,
        this.data.flexible
      );
      quota.percentage = Math.round(
        (quota.number * 100) / Number(this.data.complete)
      );
      quota.minimum = minimum;
      quota.maximum = maximum;
    });
    this.data = { ...this.data };
    this.verifyDistribution();
  }

  clearDistribution() {
    const quotas = this.data.quotas;
    quotas.forEach((quota) => {
      quota.number = 0;
    });
    this.data = { ...this.data, quotas };
    this.totalCompletes = 0;
    this.remaning = this.data.complete;
    this._updateQuotasValue();
  }

  ngOnDestroy(): void {
    this._subscription.unsubscribe();
  }
}
